import { useCallback } from 'react'

import type { DataTablePagination } from './data-table.interface'

interface UseDataTablePaginationProps {
  pagination: DataTablePagination
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
}

interface UseDataTablePaginationReturn {
  totalPages: number
  startItem: number
  endItem: number
  isFirstPage: boolean
  isLastPage: boolean
  handleFirstPage: () => void
  handlePrevPage: () => void
  handleNextPage: () => void
  handleLastPage: () => void
  handlePageSizeChange: (value: string) => void
}

/**
 * Hook for managing data table pagination state and handlers.
 * Provides page navigation handlers and computed pagination values.
 */
export const useDataTablePagination = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}: UseDataTablePaginationProps): UseDataTablePaginationReturn => {
  const totalPages = Math.ceil(pagination.totalCount / pagination.pageSize)
  const startItem =
    pagination.totalCount === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1
  const endItem = Math.min(pagination.page * pagination.pageSize, pagination.totalCount)

  const isFirstPage = pagination.page <= 1
  const isLastPage = pagination.page >= totalPages

  const handleFirstPage = useCallback((): void => {
    onPageChange?.(1)
  }, [onPageChange])

  const handlePrevPage = useCallback((): void => {
    if (!isFirstPage) {
      onPageChange?.(pagination.page - 1)
    }
  }, [isFirstPage, onPageChange, pagination.page])

  const handleNextPage = useCallback((): void => {
    if (!isLastPage) {
      onPageChange?.(pagination.page + 1)
    }
  }, [isLastPage, onPageChange, pagination.page])

  const handleLastPage = useCallback((): void => {
    onPageChange?.(totalPages)
  }, [onPageChange, totalPages])

  const handlePageSizeChange = useCallback(
    (value: string): void => {
      onPageSizeChange?.(Number.parseInt(value, 10))
    },
    [onPageSizeChange]
  )

  return {
    totalPages,
    startItem,
    endItem,
    isFirstPage,
    isLastPage,
    handleFirstPage,
    handlePrevPage,
    handleNextPage,
    handleLastPage,
    handlePageSizeChange,
  }
}
